import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { Row, Col, ButtonGroup, Button, Card, CardHeader, CardBody, CardTitle } from 'reactstrap';
import { Link } from "react-router-dom";
import { TableActions, PageTitle } from '../../../components';
import { Filters, Utils, isSuperAdmin } from '../../../services/';
import { Invoice } from '../../../models';
import matchSorter from 'match-sorter';
import _ from 'lodash';

import ReactTable from 'react-table-6';
import 'react-table-6/react-table.css'

import './InvoicesList.css';
import { loadGroups, loadInvoices, generateInvoices } from '../../../redux/modules';
import Table from '../../../components/Table/Table';

import Icon from '@mdi/react';
import {
    mdiAccountCreditCardOutline,
    mdiFaceMan,
    mdiFaceWoman,
    mdiCheckDecagramOutline,
    mdiAlertCircleOutline,
    mdiPlus,
    mdiLightningBoltOutline
} from '@mdi/js';


const InvoicesList = props => {
    const dispatch = useDispatch();

    const { groups } = useSelector(state => state.groups);

    useEffect(() => {
        dispatch(loadGroups());
        dispatch(loadInvoices());
    }, [])

    const _generateInvoices = () => {
        dispatch(generateInvoices());
    };

    const { t } = useTranslation();
    const {
        invoices,
        inactive_invoices,
        hasLoadingError,
        loadingErrorMessage,
        invoicesSuccessfullyGenerated,
    } = useSelector(state => state.invoices);

    const default_lang = Utils.default_lang();

    const cellProps = cellInfo => {
        switch (cellInfo.column.id) {
            case 'amount':
                return {
                    className: `text-center status-${cellInfo.row.original.status}`
                };
            default:
                return {};
        }
    };

    const columns = React.useMemo(() =>[{
        Header: t('invoices.amount'),
        accessor: "amount",
        filterMethod: Filters.textFilterMethod("amount"),
        Filter: Filters.textFilter,
        filterAll: true,
        width: '10%',
        className: 'text-center',
        Cell: cell => (
            <div className={`status-${cell.row.original.status}`}>
                {  cell.value + ' €' }
            </div>
        )
    }, {
        Header: t('invoices.paid'),
        accessor: "paid",
        filterMethod: Filters.textFilterMethod("paid"),
        Filter: Filters.textFilter,
        filterAll: true,
        width: '10%',
        className: 'text-center',
        Cell: row => row.value + ' €'
    }, {
        Header: t('invoices.due'),
        accessor: "due",
        filterMethod: Filters.textFilterMethod("due"),
        Filter: Filters.textFilter,
        filterAll: true,
        width: '10%',
        className: 'text-center',
        Cell: row => row.value + ' €'
    }, {
        Header: t('invoices.lastname'),
        accessor: "student.lastname",
        filterMethod: Filters.textFilterMethod("student.rfullname"),
        Filter: Filters.textFilter,
        filterAll: true,
        width: '30%',
        Cell: row => (
            <strong>
                <Icon path={ row.original?.student?.gender === 'F' ? mdiFaceWoman : mdiFaceMan } size={1} />&nbsp;
                { row.value }
            </strong>
        )
    }, {
        Header: t('invoices.firstname'),
        accessor: "student.firstname",
        filterMethod: Filters.textFilterMethod("student.firstname"),
        Filter: Filters.textFilter,
        width: '30%',
        filterAll: true,
    }, {
        Header: t('common.actions'),
        accessor: "id",
        width: '10%',
        filterable: false,
        className: 'text-center',
        Cell: row => <TableActions name="invoices" id={row.value} />
    }], [groups]);

    const actions =
    <div>
        <ButtonGroup>
            <Button onClick={_generateInvoices} color="danger" size="sm">
                <Icon path={ mdiLightningBoltOutline } size={1} />&nbsp;
                {invoicesSuccessfullyGenerated && t('invoices.generated', {x: invoices?.length})}
                {!invoicesSuccessfullyGenerated && t('invoices.generate')}
            </Button>
            <Button tag={Link} to="/invoices/new" color="success" size="sm">
                <Icon path={ mdiPlus } size={1} />&nbsp;
                {t('invoices.add_a_invoice')}
            </Button>
        </ButtonGroup>
    </div>;

    const total = React.useMemo(() => {
        return invoices?.reduce((memo, invoice) => memo + invoice.amount, 0);
    }, [invoices]);

    const paid = React.useMemo(() => {
        return invoices?.reduce((memo, invoice) => memo + invoice.paid, 0);
    }, [invoices]);

    const due = React.useMemo(() => {
        return invoices?.reduce((memo, invoice) => memo + invoice.due, 0);
    }, [invoices]);

    return (
        <div className="InvoicesList">
            <PageTitle icon={mdiAccountCreditCardOutline} title={t('invoices.invoice_plural')} actions={ actions } />
            {invoices && (
            <Row className="row-flex justify-content-md-start">
                <Col md={4}>
                    <Card color="dark" className="text-dark h-100">
                        <CardBody className="d-flex align-items-center">
                            <CardTitle tag="h1" className="align-items-center text-light">
                                <small>
                                    { t('invoices.total') } :&nbsp;
                                </small>
                                <strong>
                                    { Invoice.format(total) }
                                </strong>
                            </CardTitle>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card color="light" className="text-dark h-100">
                        <CardBody className="d-flex align-items-center">
                            <CardTitle tag="h1" className="align-items-center">
                                <small>
                                    { t('invoices.paid') } :&nbsp;
                                </small>
                                <strong>
                                    { Invoice.format(paid) }
                                </strong>
                            </CardTitle>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card color="light" className="text-dark h-100">
                        <CardBody className="d-flex align-items-center">
                            <CardTitle tag="h1" className="align-items-center">
                                <small>
                                    { t('invoices.due') } :&nbsp;
                                </small>
                                <strong>
                                    { Invoice.format(due) }
                                </strong>
                            </CardTitle>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            )}
            { invoices && (
                <Table
                    id="invoicesTable"
                    data={ invoices }
                    columns={ columns }
                    color="light" striped hover bordered sm responsive
                    getCellProps={ cellProps }
                />
            )}
        </div>
    );
}

export default InvoicesList;
